import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trash2, Wand2, Sparkles, ChevronDown, ChevronUp, Download, Globe, Lock, Copy, Github, Repeat, XCircle, Flame } from "lucide-react";
import { toast } from "sonner";
import BlueprintCard from "../components/BlueprintCard";
import PlatformPicker from "../components/PlatformPicker";
import SavedShelf from "../components/SavedShelf";
import ProgressJournal from "../components/ProgressJournal";
import TrophyCabinet from "../components/TrophyCabinet";
import MarketingPreview from "../components/MarketingPreview";
import GitHubConnect from "../components/GitHubConnect";
import PushToGitHubDialog from "../components/PushToGitHubDialog";

export default function Dashboard() {
  const { user } = useAuth();
  const [bps, setBps] = useState([]);
  const [progress, setProgress] = useState([]);
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(null);
  const [busyShare, setBusyShare] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [marketingFor, setMarketingFor] = useState(null);
  const [marketingPlatforms, setMarketingPlatforms] = useState(["ios"]);
  const [pushFor, setPushFor] = useState(null);
  const [subs, setSubs] = useState([]);
  const [cancelingSub, setCancelingSub] = useState(null);
  const [streak, setStreak] = useState(null);
  const [recap, setRecap] = useState(null);
  const [recapHistory, setRecapHistory] = useState(null);   // null = not loaded, [] = empty
  const [nextRecap, setNextRecap] = useState(null);       // Saturday "Tomorrow's letter" preview
  const [showRecapHistory, setShowRecapHistory] = useState(false);
  const [countryDraft, setCountryDraft] = useState("");
  const [savingCountry, setSavingCountry] = useState(false);

  const load = () => {
    api.get("/prompt/blueprints").then(({ data }) => setBps(data));
    api.get("/lessons/progress/me").then(({ data }) => setProgress(data));
    api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/paypal/subscriptions").then(({ data }) => setSubs(data.subscriptions || [])).catch(() => {});
    api.get("/quiz/streak/me").then(({ data }) => setStreak(data)).catch(() => {});
    api.get("/user/me/last-recap").then(({ data }) => setRecap(data)).catch(() => {});
    api.get("/user/me/next-recap-preview").then(({ data }) => setNextRecap(data)).catch(() => {});
  };

  const saveCountry = async () => {
    const v = countryDraft.trim().slice(0, 40);
    if (!v) return;
    setSavingCountry(true);
    try {
      await api.patch("/auth/profile", { country: v });
      toast.success(`Country saved as ${v}`);
      setCountryDraft("");
      // Reload user so the streak card / other places see it.
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save country");
    } finally {
      setSavingCountry(false);
    }
  };

  const loadRecapHistory = () => {
    if (recapHistory) { setShowRecapHistory((s) => !s); return; }
    api.get("/user/me/recaps?limit=4").then(({ data }) => {
      setRecapHistory(data.rows || []);
      setShowRecapHistory(true);
    }).catch(() => setRecapHistory([]));
  };
  useEffect(() => { load(); }, []);

  const cancelSubscription = async (sub) => {
    if (!window.confirm(`Cancel your ${sub.tier} subscription? You'll keep access until the current billing period ends.`)) return;
    setCancelingSub(sub.paypal_subscription_id);
    try {
      await api.post(`/paypal/subscriptions/${sub.paypal_subscription_id}/cancel`);
      toast.success("Subscription cancelled — access continues until period end");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelingSub(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this blueprint?")) return;
    try {
      await api.delete(`/prompt/blueprints/${id}`);
      setBps(bps.filter(b => b.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const toggleShare = async (bp) => {
    setBusyShare(bp.id);
    const next = !bp.is_public;
    try {
      await api.post(`/prompt/blueprints/${bp.id}/share`, { is_public: next });
      setBps(bps.map(b => b.id === bp.id ? { ...b, is_public: next } : b));
      toast.success(next ? "Now visible in the App Atlas" : "Made private");
    } catch {
      toast.error("Could not update sharing");
    } finally {
      setBusyShare(null);
    }
  };

  const copyShareUrl = async (bp) => {
    const url = `${window.location.origin}/gallery/${bp.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const exportZip = async (bp, platforms, opts = {}) => {
    if (opts.marketing) {
      setMarketingPlatforms(platforms);
      setPickerFor(null);
      setMarketingFor(bp);
      return;
    }
    setExporting(true);
    try {
      const token = localStorage.getItem("dpa_token");
      const params = new URLSearchParams({ platforms: platforms.join(",") });
      const res = await fetch(`${API}/prompt/blueprints/${bp.id}/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      downloadBlob(blob, bp.title);
      toast.success("Starter kit downloaded");
      setPickerFor(null);
    } catch {
      toast.error("Could not export");
    } finally {
      setExporting(false);
    }
  };

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name || "MyApp").replace(/\s+/g, "")}-starter.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-narrow py-12 md:py-16" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="label-eyebrow mb-2">Your workshop</div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold">
            Hi, <span className="italic text-marigold">{user?.name?.split(" ")[0]}</span>.
          </h1>
        </div>
        <Link to="/studio" className="btn-primary" data-testid="dashboard-new-blueprint">
          <Wand2 className="w-4 h-4" /> New blueprint
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Stat label="Saved blueprints" value={bps.length} testid="stat-blueprints" />
        <Stat label="Lessons completed" value={progress.length} testid="stat-lessons" />
        <Stat label="Tier" value={user?.tier || "free"} testid="stat-tier" />
      </div>

      {streak && (streak.current > 0 || streak.best > 0) && (
        <div className="card-soft p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="dashboard-streak-card">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl border-2 border-marigold bg-marigold/10 flex items-center justify-center ${streak.stale ? "opacity-60" : ""}`}>
              <Flame className="w-7 h-7 text-marigold" />
            </div>
            <div>
              <div className="label-eyebrow">Study-mode streak</div>
              <div className="font-heading text-3xl font-semibold" data-testid="dashboard-streak-current">
                {streak.current} <span className="text-sm text-ink2 font-normal">day{streak.current === 1 ? "" : "s"}</span>
              </div>
              <div className="text-xs text-ink2 mt-0.5">
                Best · <span className="font-mono text-marigold" data-testid="dashboard-streak-best">{streak.best}</span>
                {streak.total_attempts ? <> · {streak.total_attempts} quiz{streak.total_attempts === 1 ? "" : "zes"} completed</> : null}
                {streak.stale ? <> · <span className="text-ink2 italic">went cold — quiz today to restart</span></> : null}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/mendel/ide" className="btn-outline-ink text-xs" data-testid="dashboard-streak-cta">
              <Sparkles className="w-3 h-3" /> Keep the streak alive
            </Link>
            <Link to="/leaderboard" className="text-[10px] uppercase tracking-widest font-bold text-marigold hover:underline" data-testid="dashboard-streak-leaderboard">
              🏆 Leaderboard
            </Link>
          </div>
        </div>
      )}

      {/* Country nudge — only shown when unset */}
      {user && !user.country && (
        <div className="card-soft p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="dashboard-country-nudge">
          <Globe className="w-5 h-5 text-marigold shrink-0" />
          <div className="text-sm text-ink2 flex-1 min-w-[220px]">
            <span className="font-heading text-ink">Add your country</span> so friends nearby can chase your best streak on the leaderboard.
          </div>
          <input
            value={countryDraft}
            onChange={(e) => setCountryDraft(e.target.value)}
            placeholder="IN, US, BR…"
            className="px-3 py-1.5 rounded-md border-2 border-ink bg-cream text-sm w-28 font-mono"
            maxLength={40}
            data-testid="dashboard-country-input"
          />
          <button
            onClick={saveCountry}
            disabled={savingCountry || !countryDraft.trim()}
            className="btn-primary text-xs"
            data-testid="dashboard-country-save"
          >
            {savingCountry ? "Saving…" : "Save"}
          </button>
        </div>
      )}

      {/* Your Notebook — saved chapters from the curriculum ready to read */}
      <div className="mb-6">
        <SavedShelf variant="compact" limit={8} />
      </div>

      {/* Progress Journal — concepts you've explored across the twelve worlds */}
      <div className="mb-6">
        <ProgressJournal />
      </div>

      {/* Trophy Cabinet — finished-adventure certificates */}
      <div className="mb-6">
        <TrophyCabinet />
      </div>

      {/* Sunday preview — tease tomorrow's letter every Saturday */}
      {nextRecap && nextRecap.show_today && Array.isArray(nextRecap.recap?.espresso) && nextRecap.recap.espresso.length > 0 && (
        <section
          className="mb-6 rounded-3xl border-2 border-royal bg-gradient-to-br from-gold/25 via-parchment to-marigold/10 shadow-[8px_8px_0_0_#2E1A47] overflow-hidden"
          data-testid="dashboard-sunday-preview"
        >
          <div className="px-6 pt-5 pb-3 border-b border-royal/15 flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-1 inline-flex items-center gap-2">
                Tomorrow's Sunday letter
                <span className="text-[9px] tracking-widest font-bold text-royal bg-gold/60 border border-royal rounded-full px-1.5 py-px normal-case">Preview</span>
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-royal leading-tight">
                Three chapters Ms. Pradhan has already picked.
              </h3>
              <p className="text-sm text-ink2 mt-1 italic">
                {nextRecap.recap.body_note || `Landing in your inbox ${nextRecap.will_send_on}.`}
              </p>
            </div>
          </div>
          <ul className="divide-y divide-royal/10">
            {nextRecap.recap.espresso.slice(0, 3).map((a) => (
              <li key={a.slug}>
                <Link
                  to={`/library/${a.slug}`}
                  className="flex items-start gap-3 px-6 py-3 hover:bg-royal/[0.04] transition-colors group"
                  data-testid={`sunday-preview-${a.code || a.slug}`}
                >
                  <div className="text-[10px] font-mono font-bold text-royal/70 pt-0.5 whitespace-nowrap">
                    {a.module_code}·A{String(a.article_num || 0).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-sm font-semibold text-royal group-hover:text-marigold transition-colors leading-snug">
                      {a.title}
                    </div>
                    {a.summary && (
                      <div className="text-[11px] text-ink2 mt-0.5 line-clamp-2 leading-relaxed">
                        {a.summary}
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Recap preview — the last Sunday letter */}
      {recap && recap.recap && (
        <div className="card-soft p-5 mb-6" data-testid="dashboard-recap-card">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="label-eyebrow">Your Sunday letter</div>
              <div className="font-heading text-2xl">Week {recap.recap.week}</div>
            </div>
            <div className="text-[10px] font-mono text-ink2">
              {recap.recap.sent ? "sent" : <span className="text-ink2/70">would-send · email not configured</span>}
              {" · "}{new Date(recap.recap.created_at).toLocaleDateString()}
            </div>
          </div>
          {recap.recap.recap && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-3" data-testid="dashboard-recap-stats">
                <Stat label="Streak" value={`🔥 ${recap.recap.recap.streak?.current ?? 0}`} testid="recap-stat-streak" />
                <Stat label="Best" value={recap.recap.recap.streak?.best ?? 0} testid="recap-stat-best" />
                <Stat label="Quizzes" value={recap.recap.recap.quiz_count ?? 0} testid="recap-stat-quizzes" />
                <Stat label="Stops" value={recap.recap.recap.journey_stops ?? 0} testid="recap-stat-stops" />
              </div>
              {recap.recap.recap.body_note && (
                <p className="text-sm text-ink2 italic border-l-2 border-marigold pl-3 mb-3" data-testid="dashboard-recap-note">
                  “{recap.recap.recap.body_note}”
                </p>
              )}
              {Array.isArray(recap.recap.recap.espresso) && recap.recap.recap.espresso.length > 0 && (
                <div className="mb-3 rounded-2xl border-2 border-royal/70 bg-parchment/70 overflow-hidden" data-testid="dashboard-recap-espresso">
                  <div className="px-4 py-2 border-b border-royal/15 bg-gold/15">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-gold inline-flex items-center gap-1.5">
                      <span>Three chapters from your road trip this week</span>
                    </div>
                  </div>
                  <ul className="divide-y divide-royal/10">
                    {recap.recap.recap.espresso.slice(0, 3).map((a) => (
                      <li key={a.slug}>
                        <Link
                          to={`/library/${a.slug}`}
                          className="flex items-start gap-3 px-4 py-2.5 hover:bg-royal/[0.04] transition-colors group"
                          data-testid={`dashboard-recap-espresso-${a.code || a.slug}`}
                        >
                          <div className="text-[10px] font-mono font-bold text-royal/70 pt-0.5 whitespace-nowrap">
                            {a.module_code}·A{String(a.article_num || 0).padStart(2, "0")}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-heading text-sm font-semibold text-royal group-hover:text-marigold transition-colors leading-snug">
                              {a.title}
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          <button
            onClick={loadRecapHistory}
            className="text-[10px] uppercase tracking-widest font-bold text-marigold hover:underline"
            data-testid="dashboard-recap-history-toggle"
          >
            {showRecapHistory ? "Hide" : "Peek at previous weeks"} →
          </button>
          {showRecapHistory && recapHistory && (
            <ul className="mt-3 space-y-2" data-testid="dashboard-recap-history">
              {recapHistory.length === 0 && <li className="text-xs text-ink2 italic">No older recaps yet.</li>}
              {recapHistory.map((row, i) => (
                <li key={`${row.week}-${row.created_at || i}`} className="border-t border-ink2/10 pt-2 text-xs flex items-center justify-between gap-3">
                  <div>
                    <span className="font-mono text-marigold">{row.week}</span>
                    <span className="ml-2 text-ink2">🔥 {row.recap?.streak?.current ?? 0} · {row.recap?.quiz_count ?? 0} quizzes · {row.recap?.journey_stops ?? 0} stops</span>
                  </div>
                  <span className="text-[10px] font-mono text-ink2">{row.sent ? "sent" : "would-send"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {recap && !recap.recap && (
        <div className="card-soft p-4 mb-6 text-sm text-ink2 italic" data-testid="dashboard-recap-empty">
          {recap.hint}
        </div>
      )}

      {usage && (usage.credits_used > 0 || (usage.ledger || []).length > 0) && (
        <div className="card-soft p-5 mb-6" data-testid="dash-credits-ledger">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">Credits ledger</div>
              <div className="text-2xl font-heading font-semibold mt-0.5">
                {usage.credits_used} <span className="text-sm text-ink2 font-normal">credits used</span>
              </div>
            </div>
            <div className="text-xs text-ink2 max-w-xs text-right">
              <span className="font-semibold text-ink">Per-action visibility.</span> Billing isn&apos;t wired yet — this is just so you can see what each action costs as we move toward credit-based pricing.
            </div>
          </div>
          {(usage.ledger || []).length > 0 && (
            <ul className="text-xs text-ink2 space-y-1 max-h-40 overflow-y-auto pr-2">
              {usage.ledger.slice(0, 12).map((e, i) => (
                <li key={`${e.at || "no-at"}-${i}`} className="flex items-center justify-between gap-3 font-mono" data-testid={`ledger-row-${i}`}>
                  <span className="truncate flex-1">
                    <span className="text-ink font-bold">{e.action}</span>
                    {e.meta?.title && <span className="text-ink2"> · {e.meta.title}</span>}
                  </span>
                  <span className="text-marigold font-bold">−{e.cost}</span>
                  <span className="text-ink2/60 text-[10px] w-16 text-right">{(e.at || "").slice(5, 16).replace("T", " ")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mb-10">
        <GitHubConnect />
      </div>

      {subs.length > 0 && (
        <section className="mb-10" data-testid="dash-subscriptions">
          <h2 className="font-heading text-2xl font-semibold mb-5 inline-flex items-center gap-2">
            <Repeat className="w-5 h-5 text-marigold" /> Subscriptions
          </h2>
          <div className="space-y-3">
            {subs.map((s) => {
              const active = s.status === "ACTIVE";
              const cancelled = s.status === "CANCELLED" || s.cancel_at_period_end;
              return (
                <div key={s.paypal_subscription_id} className="card-soft p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`sub-row-${s.paypal_subscription_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading text-lg font-semibold capitalize">{s.tier}</span>
                      <span className="text-xs text-ink2 uppercase tracking-widest font-semibold">{s.interval_unit === "YEAR" ? "Yearly" : "Monthly"}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${active && !cancelled ? "bg-teal/15 text-teal" : cancelled ? "bg-marigold/15 text-marigold" : "bg-paper text-ink2"}`}>
                        {cancelled ? "Cancels at period end" : s.status}
                      </span>
                    </div>
                    <div className="text-xs text-ink2 mt-1 font-mono truncate">
                      {s.paypal_subscription_id}
                      {s.next_billing_time && !cancelled && <> · next renewal {new Date(s.next_billing_time).toLocaleDateString()}</>}
                    </div>
                  </div>
                  {active && !cancelled && (
                    <button
                      onClick={() => cancelSubscription(s)}
                      disabled={cancelingSub === s.paypal_subscription_id}
                      className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-ink2 hover:border-marigold hover:text-marigold transition disabled:opacity-60"
                      data-testid={`sub-cancel-${s.paypal_subscription_id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" /> {cancelingSub === s.paypal_subscription_id ? "Cancelling…" : "Cancel"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <h2 className="font-heading text-2xl font-semibold mb-5 inline-flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-marigold" /> Saved blueprints
      </h2>

      {bps.length === 0 ? (
        <div className="card-soft p-10 text-center" data-testid="dashboard-empty">
          <p className="text-ink2 mb-4">No blueprints yet. Spin one up in the Studio.</p>
          <Link to="/studio" className="btn-outline-ink">Open Studio</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bps.map(bp => (
            <div key={bp.id} className="card-soft" data-testid={`blueprint-row-${bp.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-xl font-semibold truncate">{bp.title}</span>
                    {bp.is_public && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal/15 text-teal text-[10px] font-bold uppercase tracking-wider">
                        <Globe className="w-3 h-3" /> Public
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-ink2 truncate">{bp.prompt}</div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggleShare(bp)}
                    disabled={busyShare === bp.id}
                    className={`text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition ${bp.is_public ? "border-teal text-teal hover:bg-teal hover:text-cream" : "border-border text-ink2 hover:border-ink hover:text-ink"}`}
                    data-testid={`bp-share-${bp.id}`}
                  >
                    {bp.is_public ? <><Globe className="w-3.5 h-3.5" /> Public</> : <><Lock className="w-3.5 h-3.5" /> Private</>}
                  </button>
                  {bp.is_public && (
                    <button onClick={() => copyShareUrl(bp)} className="btn-ghost text-sm py-1.5" title="Copy share link" data-testid={`bp-copy-link-${bp.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => setPickerFor(bp)} className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-ink2 hover:border-ink hover:text-ink transition" data-testid={`bp-export-${bp.id}`}>
                    <Download className="w-3.5 h-3.5" /> Export
                  </button>
                  <button onClick={() => setPushFor(bp)} className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-ink2 hover:border-ink hover:text-ink transition" data-testid={`bp-push-${bp.id}`} title="Push to GitHub">
                    <Github className="w-3.5 h-3.5" /> Push
                  </button>
                  <button onClick={() => setOpen(open === bp.id ? null : bp.id)} className="btn-ghost text-sm" data-testid={`bp-toggle-${bp.id}`}>
                    {open === bp.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => remove(bp.id)} className="text-marigold hover:text-marigold/80 p-2" data-testid={`bp-delete-${bp.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {open === bp.id && (
                <div className="border-t border-border p-5">
                  <BlueprintCard bp={bp} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <PlatformPicker
        open={!!pickerFor}
        onOpenChange={(o) => !o && setPickerFor(null)}
        onConfirm={(platforms, opts) => exportZip(pickerFor, platforms, opts)}
        busy={exporting}
        title={pickerFor ? `Export "${pickerFor.title}"` : "Export"}
      />

      <MarketingPreview
        open={!!marketingFor}
        onOpenChange={(o) => !o && setMarketingFor(null)}
        blueprint={marketingFor}
        platforms={marketingPlatforms}
      />

      <PushToGitHubDialog
        open={!!pushFor}
        onOpenChange={(o) => !o && setPushFor(null)}
        blueprint={pushFor}
        platforms={["ios"]}
      />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-heading text-3xl font-semibold capitalize">{value}</div>
    </div>
  );
}
